import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const tocItems = [
  { id: 'what-is', label: 'What Is AllChinaBuy Spreadsheet?' },
  { id: 'how-to-use', label: 'How to Use AllChinaBuy Spreadsheet' },
  { id: 'categories', label: 'Product Categories Available' },
  { id: 'benefits', label: 'Benefits of Using AllChinaBuy Spreadsheet' },
  { id: 'shipping', label: 'Understanding Shipping & Costs' },
  { id: 'qc-guide', label: 'Quality Check (QC) Guide' },
  { id: 'coupons', label: 'Coupons & Discount Codes' },
  { id: 'comparisons', label: 'AllChinaBuy vs Other Agents' },
  { id: 'safety', label: 'Is AllChinaBuy Safe?' },
  { id: 'tips', label: 'Pro Tips for Smart Shopping' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'allchinabuy-review', title: 'AllChinaBuy Review 2026', readTime: 8 },
  { slug: 'allchinabuy-shipping-cost', title: 'AllChinaBuy Shipping Cost Guide', readTime: 10 },
  { slug: 'allchinabuy-coupon', title: 'AllChinaBuy Coupon & Discount Codes', readTime: 6 },
  { slug: 'allchinabuy-vs-superbuy', title: 'AllChinaBuy vs Competitors', readTime: 9 },
];

export default function AllChinaBuySpreadsheetGuide() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );

    tocItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 bg-white border-b border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <nav className="flex items-center gap-2 text-sm text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors cursor-pointer">{t('breadcrumb_home')}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-stone-900 font-medium">AllChinaBuy Spreadsheet Complete Guide</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-8 md:py-12">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Sidebar TOC */}
          <aside className="lg:w-64 lg:flex-shrink-0 hidden lg:block">
            <div className="sticky top-24 bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-4">{t('article_toc')}</h3>
              <nav className="space-y-1">
                {tocItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                      activeSection === item.id
                        ? 'bg-stone-100 text-stone-900 font-medium'
                        : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'
                    }`}
                    type="button"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <article className="flex-1 min-w-0">
            {/* Header */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm mb-8">
              <div className="relative h-64 md:h-80 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Flat%20lay%20of%20premium%20fashion%20products%20spreadsheet%20on%20laptop%20screen%20with%20sneakers%20clothing%20and%20accessories%20arranged%20on%20clean%20white%20desk%2C%20bright%20natural%20light%20from%20window%2C%20minimalist%20editorial%20photography%20style%2C%20warm%20neutral%20color%20palette%2C%20professional%20product%20photography&width=1200&height=600&seq=article-hero-01&orientation=landscape"
                  alt="AllChinaBuy Spreadsheet Complete Guide"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-white/90 backdrop-blur-sm rounded-full text-stone-700 mb-3">
                    12 min read
                  </span>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                    AllChinaBuy Spreadsheet Complete Guide: Your Ultimate Resource for 2026
                  </h1>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500 mb-6">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {t('article_last_updated')}: August 25, 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {t('article_author')}: AllChinaBuy Research Team
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {t('article_reading_time')}: 12 min
                  </span>
                </div>
                <p className="text-base md:text-lg text-stone-600 leading-relaxed">
                  Looking for the most comprehensive guide to AllChinaBuy spreadsheet resources? You've found it. This complete guide covers everything from understanding what AllChinaBuy spreadsheets are, how to use them effectively, product categories, shipping costs, quality checks, and expert tips to maximize your shopping experience in 2026.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8 lg:p-10 shadow-sm space-y-10">
              {/* Section 1 */}
              <section id="what-is">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">What Is AllChinaBuy Spreadsheet?</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  <strong>AllChinaBuy Spreadsheet</strong> is a curated collection of product links, prices, and seller information organized in an easy-to-use format. It helps international shoppers find the best deals on shoes, clothing, accessories, and more from Chinese shopping platforms. These spreadsheets are designed to save time by consolidating product research into one centralized resource.
                </p>
                <p className="text-stone-600 leading-relaxed mb-4">
                  Unlike browsing individual marketplace listings, AllChinaBuy spreadsheets provide organized categories with direct links to verified products. This eliminates the need to navigate complex Chinese e-commerce platforms and helps shoppers make informed purchasing decisions quickly.
                </p>
                <p className="text-stone-600 leading-relaxed">
                  The spreadsheets are regularly updated to reflect current prices, availability, and new product releases. This makes them an invaluable tool for anyone looking to purchase fashion items, electronics, and accessories from Chinese suppliers through the AllChinaBuy shopping agent service.
                </p>
              </section>

              {/* Section 2 */}
              <section id="how-to-use">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">How to Use AllChinaBuy Spreadsheet</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  Using the AllChinaBuy spreadsheet is straightforward once you understand the structure. Here's a step-by-step guide to get you started:
                </p>
                <div className="bg-stone-50 rounded-xl p-5 md:p-6 space-y-4 mb-4">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Browse Categories</h4>
                      <p className="text-sm text-stone-600">Start by selecting your desired product category from the organized list. Categories include shoes, hoodies, t-shirts, jackets, pants, accessories, and more.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Click Product Links</h4>
                      <p className="text-sm text-stone-600">Each product in the spreadsheet has a direct link to its marketplace listing. Click to view detailed product information, images, and pricing.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Add to Cart</h4>
                      <p className="text-sm text-stone-600">When you find items you want to purchase, add them to your AllChinaBuy cart through the provided links or copy the product URL into the agent's purchase system.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">4</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Complete Purchase</h4>
                      <p className="text-sm text-stone-600">Proceed with the purchasing process through AllChinaBuy. Your items will be sent to the AllChinaBuy warehouse for quality inspection before international shipping.</p>
                    </div>
                  </div>
                </div>
                <p className="text-stone-600 leading-relaxed">
                  For best results, we recommend creating an account on AllChinaBuy first and familiarizing yourself with the platform's interface before making your first purchase through spreadsheet links.
                </p>
              </section>

              {/* Section 3 */}
              <section id="categories">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Product Categories Available</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  AllChinaBuy spreadsheets cover a wide range of product categories. Here's a comprehensive overview of what's available:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {[
                    { name: 'Shoes', desc: 'Sneakers, running shoes, casual footwear, designer shoes', icon: 'ri-footprint-line' },
                    { name: 'Hoodies & Sweaters', desc: 'Streetwear, designer hoodies, knitwear, pullovers', icon: 'ri-shirt-line' },
                    { name: 'T-Shirts', desc: 'Graphic tees, designer shirts, basics, branded tops', icon: 'ri-t-shirt-line' },
                    { name: 'Jackets', desc: 'Bombers, windbreakers, puffer jackets, denim jackets', icon: 'ri-windy-line' },
                    { name: 'Pants & Shorts', desc: 'Jeans, cargo pants, joggers, athletic shorts', icon: 'ri-layout-bottom-2-line' },
                    { name: 'Headwear', desc: 'Caps, beanies, bucket hats, designer headwear', icon: 'ri-headphone-line' },
                    { name: 'Sets & Outfits', desc: 'Matching tracksuits, coordinated outfits, sportswear sets', icon: 'ri-stack-line' },
                    { name: 'Accessories', desc: 'Watches, jewelry, sunglasses, phone cases, belts', icon: 'ri-star-line' },
                    { name: 'Bags', desc: 'Backpacks, crossbody bags, tote bags, designer handbags', icon: 'ri-shopping-bag-3-line' },
                    { name: 'Perfumes', desc: 'Designer fragrances, colognes, perfume samples', icon: 'ri-drop-line' },
                  ].map((cat, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl">
                      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-lg text-stone-600`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-stone-900 text-sm">{cat.name}</h4>
                        <p className="text-xs text-stone-500 mt-0.5">{cat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-stone-600 leading-relaxed">
                  Each category is regularly updated with new finds, trending items, and seasonal collections. The spreadsheet format allows you to quickly scan multiple products and compare options before making a purchase decision.
                </p>
              </section>

              {/* Section 4 */}
              <section id="benefits">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Benefits of Using AllChinaBuy Spreadsheet</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  There are several compelling reasons to use AllChinaBuy spreadsheets for your shopping needs:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-500 mt-1 flex-shrink-0"></i>
                    <span className="text-stone-600"><strong>Time Savings:</strong> No more browsing multiple platforms or searching through countless listings. Everything is organized in one place.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-500 mt-1 flex-shrink-0"></i>
                    <span className="text-stone-600"><strong>Verified Links:</strong> All product links are tested and verified, reducing the risk of dead links or incorrect listings.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-500 mt-1 flex-shrink-0"></i>
                    <span className="text-stone-600"><strong>Price Transparency:</strong> Compare prices across similar products at a glance to ensure you're getting the best deal.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-500 mt-1 flex-shrink-0"></i>
                    <span className="text-stone-600"><strong>Curated Selection:</strong> Products are hand-picked and categorized, saving you from sorting through low-quality or irrelevant items.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-500 mt-1 flex-shrink-0"></i>
                    <span className="text-stone-600"><strong>Regular Updates:</strong> Spreadsheets are updated frequently with new products, price changes, and availability status.</span>
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section id="shipping">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Understanding Shipping & Costs</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  Shipping is one of the most important considerations when using AllChinaBuy. Understanding how shipping works and what costs to expect can help you budget effectively and avoid surprises.
                </p>
                <p className="text-stone-600 leading-relaxed mb-4">
                  AllChinaBuy offers multiple shipping lines to different countries. The main options typically include EMS, DHL, FedEx, and various budget-friendly postal services. Shipping costs depend on your destination country, the weight and dimensions of your parcel, and the shipping method you choose.
                </p>
                <div className="bg-stone-50 rounded-xl p-5 md:p-6 mb-4">
                  <h4 className="font-semibold text-stone-900 mb-3">Factors Affecting Shipping Cost:</h4>
                  <ul className="space-y-2 text-sm text-stone-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-stone-400 mt-0.5"></i>
                      <span>Destination country and shipping route</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-stone-400 mt-0.5"></i>
                      <span>Parcel weight and volumetric dimensions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-stone-400 mt-0.5"></i>
                      <span>Selected shipping method (express vs. economy)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-stone-400 mt-0.5"></i>
                      <span>Insurance and declared value options</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-stone-400 mt-0.5"></i>
                      <span>Customs duties and import taxes in your country</span>
                    </li>
                  </ul>
                </div>
                <p className="text-stone-600 leading-relaxed">
                  We strongly recommend using AllChinaBuy's shipping calculator before finalizing your order. Additionally, consolidating multiple items into one shipment can significantly reduce per-item shipping costs.
                </p>
              </section>

              {/* Section 6 */}
              <section id="qc-guide">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Quality Check (QC) Guide</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  The Quality Check (QC) service is one of AllChinaBuy's most valuable features. Before your items are shipped internationally, AllChinaBuy warehouse staff will photograph your products and upload the images to your account for inspection.
                </p>
                <p className="text-stone-600 leading-relaxed mb-4">
                  This process allows you to verify that the product matches the description, check for any defects, and confirm sizing before committing to international shipping. If something is wrong, you can request a return or exchange before the item leaves China.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 md:p-6 mb-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-lightbulb-line text-amber-500 text-xl mt-0.5"></i>
                    <div>
                      <h4 className="font-semibold text-amber-900 mb-1">Pro Tip</h4>
                      <p className="text-sm text-amber-800">Always request QC photos for high-value items and products where sizing or color accuracy is critical. The small cost of QC photos is worth avoiding the disappointment of receiving an incorrect item.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 7 */}
              <section id="coupons">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Coupons & Discount Codes</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  AllChinaBuy regularly offers coupons and promotional discount codes for both new and existing customers. These can help reduce your overall costs significantly, especially when combined with other savings strategies.
                </p>
                <p className="text-stone-600 leading-relaxed mb-4">
                  Common types of discounts include first-time user coupons, seasonal promotions, referral bonuses, and shipping fee discounts. We recommend checking for available coupons before each purchase and combining them with consolidated shipping for maximum savings.
                </p>
                <p className="text-stone-600 leading-relaxed">
                  Visit our <Link to="/allchinabuy-coupon" className="text-stone-900 underline hover:no-underline cursor-pointer">AllChinaBuy Coupon Guide</Link> for the latest active discount codes and money-saving strategies.
                </p>
              </section>

              {/* Section 8 - Comparison Table */}
              <section id="comparisons">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">AllChinaBuy vs Other Shopping Agents</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  How does AllChinaBuy compare to other popular shopping agents? Here's a detailed comparison:
                </p>
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm border border-stone-200 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-stone-100">
                        <th className="px-4 py-3 text-left font-semibold text-stone-900">Feature</th>
                        <th className="px-4 py-3 text-center font-semibold text-stone-900">AllChinaBuy</th>
                        <th className="px-4 py-3 text-center font-semibold text-stone-900">Superbuy</th>
                        <th className="px-4 py-3 text-center font-semibold text-stone-900">CSSBuy</th>
                        <th className="px-4 py-3 text-center font-semibold text-stone-900">Sugargoo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      <tr>
                        <td className="px-4 py-3 text-stone-700 font-medium">Spreadsheet Resources</td>
                        <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1 text-emerald-600"><i className="ri-check-line"></i> Excellent</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-500">Limited</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-500">Basic</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-500">Moderate</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-stone-700 font-medium">QC Photos</td>
                        <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1 text-emerald-600"><i className="ri-check-line"></i> Available</span></td>
                        <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1 text-emerald-600"><i className="ri-check-line"></i> Available</span></td>
                        <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1 text-emerald-600"><i className="ri-check-line"></i> Available</span></td>
                        <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1 text-emerald-600"><i className="ri-check-line"></i> Available</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-stone-700 font-medium">Shipping Options</td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Multiple</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Multiple</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Multiple</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Multiple</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-stone-700 font-medium">User Interface</td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Clean & Simple</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Feature-rich</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Functional</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Modern</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-stone-700 font-medium">Fee Structure</td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Transparent</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Standard</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Competitive</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Standard</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-stone-700 font-medium">Customer Support</td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Good</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Good</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Good</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-stone-700">Good</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-stone-600 leading-relaxed">
                  For detailed head-to-head comparisons, check our guides comparing <Link to="/allchinabuy-vs-superbuy" className="text-stone-900 underline hover:no-underline cursor-pointer">AllChinaBuy vs Superbuy</Link>, AllChinaBuy vs CSSBuy, and AllChinaBuy vs Sugargoo.
                </p>
              </section>

              {/* Section 9 */}
              <section id="safety">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Is AllChinaBuy Safe?</h2>
                <p className="text-stone-600 leading-relaxed mb-4">
                  Safety is a top concern for anyone shopping internationally. AllChinaBuy implements several security measures to protect users, including encrypted payment processing, warehouse quality checks, and buyer protection policies.
                </p>
                <p className="text-stone-600 leading-relaxed mb-4">
                  However, as with any international shopping service, we recommend taking precautions: research sellers before purchasing, use the QC service for high-value items, avoid sharing unnecessary personal information, and be aware of potential customs regulations in your country.
                </p>
                <p className="text-stone-600 leading-relaxed">
                  For a comprehensive safety guide, visit our <Link to="/allchinabuy-legit-safety" className="text-stone-900 underline hover:no-underline cursor-pointer">Is AllChinaBuy Legit?</Link> page where we cover payment security, common scams to avoid, and best practices for safe international shopping.
                </p>
              </section>

              {/* Section 10 */}
              <section id="tips">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Pro Tips for Smart Shopping</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: 'Consolidate Shipments', desc: 'Combine multiple items into one package to reduce per-item shipping costs significantly.' },
                    { title: 'Use QC for Everything', desc: 'Always request quality check photos, especially for expensive or size-sensitive items.' },
                    { title: 'Check Size Charts', desc: 'Chinese sizing often differs from US/EU standards. Always verify measurements before ordering.' },
                    { title: 'Read Reviews', desc: 'Look for product reviews and seller ratings before committing to a purchase.' },
                    { title: 'Use Coupons', desc: 'Search for active discount codes before checkout to maximize savings.' },
                    { title: 'Plan for Customs', desc: 'Research your country\'s import duties and tax thresholds to avoid unexpected fees.' },
                  ].map((tip, i) => (
                    <div key={i} className="bg-stone-50 rounded-xl p-4">
                      <h4 className="font-semibold text-stone-900 text-sm mb-1">{tip.title}</h4>
                      <p className="text-sm text-stone-600">{tip.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ Section */}
              <section id="faq">
                <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {[
                    { q: 'How often is the AllChinaBuy spreadsheet updated?', a: 'AllChinaBuy spreadsheets are typically updated weekly or bi-weekly to reflect current prices, new product releases, and availability changes. Major updates often coincide with seasonal sales events.' },
                    { q: 'Can I request specific products to be added?', a: 'While the spreadsheets are curated by the AllChinaBuy team, you can often find specific products by using the search function or reaching out to customer support with product links.' },
                    { q: 'What if a product link is broken?', a: 'Product links can occasionally break due to marketplace changes. If you encounter a broken link, report it to the AllChinaBuy team or try searching for the product name directly on the marketplace.' },
                    { q: 'Is there a mobile app for AllChinaBuy?', a: 'AllChinaBuy primarily operates through its web interface, which is mobile-responsive. You can access all features and spreadsheet links through your mobile browser.' },
                    { q: 'How do I track my order?', a: 'Once your order ships, AllChinaBuy provides tracking numbers for most shipping methods. You can track your package through the AllChinaBuy dashboard or directly on the shipping carrier\'s website.' },
                  ].map((faq, i) => (
                    <div key={i} className="border border-stone-200 rounded-xl p-4 md:p-5">
                      <h4 className="font-semibold text-stone-900 mb-2">{faq.q}</h4>
                      <p className="text-sm text-stone-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* CTA Block */}
            <div className="mt-8 bg-stone-900 rounded-2xl p-6 md:p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-3">{t('article_cta_text')}</h3>
              <p className="text-stone-400 mb-6 max-w-lg mx-auto">Start exploring curated products, compare prices, and make informed purchases with AllChinaBuy spreadsheet resources.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-white text-stone-900 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('article_cta_button')}
                  <i className="ri-external-link-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors border border-white/20 cursor-pointer whitespace-nowrap"
                >
                  {t('article_cta_button_buy')}
                  <i className="ri-shopping-cart-line"></i>
                </a>
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-12">
              <h3 className="text-lg font-bold text-stone-900 mb-4">{t('article_related')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/${article.slug}`}
                    className="group flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200 hover:border-stone-300 transition-all cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-list-line text-stone-500 text-lg group-hover:text-stone-900 transition-colors"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-stone-900 group-hover:text-stone-700 transition-colors">{article.title}</h4>
                      <span className="text-xs text-stone-500">{article.readTime} {t('article_read_time')}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-8 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t('article_back_home')}
              </Link>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}